import { Link } from 'react-router-dom'

import Person, { People } from './Person.jsx'
import TrackTitle from './TrackTitle.jsx'
import {
  FEED_ROWS,
  MOVIE_SOURCE,
  MUSIC_SOURCE,
  WATCH_SOURCE,
  SOURCE_LABEL,
  activityFeed,
  feedAsOf,
  staleSources,
} from './activity.js'
import { freshness, shortDate, sinceLabel } from './freshness.js'
import { LOADING as MOVIE_LOADING, READY as MOVIE_READY } from './movie.js'
import { LOADING as MUSIC_LOADING, READY as MUSIC_READY } from './music.js'
import { tierText } from './vocabulary.js'
import { LOADING as WATCH_LOADING, READY as WATCH_READY } from './watch.js'
import { seasonLabel } from './watchGame.js'

// "In the server" — the cross-cog activity feed. P6-182, design §4.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 P6-242b / P6-285: THIS IS NOW THE SECOND ROW OF THE STICKY HEADER, NOT A
//    PANEL ON THE PAGE
// ═══════════════════════════════════════════════════════════════════════════
//
// Jordan, 2026-09-24: *"keep the cross cog history in one location, even if it
// needs to be small and expanded in some way to be scrolled … move it into the
// header."* The feed used to sit below the open panel for music and watch and
// beside it for movie night, trips, events and hoops — two homes depending on
// the pill, and a column taken from every panel to hold it. It is now ONE
// element in the header on every signed-in route: a freshness label and a
// chevron, the latest rows running inline where the row has room, and the whole
// list dropping in under the header on demand.
//
// The data modules did not move and did not change. `activity.js` still derives
// every row from the projections the portal already reads; what moved is WHERE
// they are read — the signed-in shell (`Layout.jsx`) hands them down — and what
// this file draws. The measurement that unblocked the feed, what it did NOT
// settle, and why there is no event bus behind it are all in `activity.js`.
//
// ⚠️ THREE THINGS THE HANDOFF ASKS FOR AND THIS FILE DELIBERATELY DOES NOT DO,
// each recorded in `docs/reports/P6-242b.md`:
//   · "TODAY ONLY". The rows are the feed's own newest rows, as they always
//     were. P6-285 is a request to keep the HISTORY in one place, and the three
//     real sources publish a handful of events a week — a today-only list would
//     be empty most of the day.
//   · "A STALE SOURCE'S ITEMS ARE LEFT OUT". P6-306 names a stuck source and
//     keeps its rows, and its tests pin that. The banner says only what is true
//     under that rule: the source last reported on such a date, so its rows may
//     be out of date.
//   · "EVERYTHING ELSE HERE IS CURRENT". `feedAsOf` records exactly why the label
//     is the freshest evidence and not a bound on every part of the feed.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 UNAVAILABLE RENDERS NOTHING
// ═══════════════════════════════════════════════════════════════════════════
//
// No source ready (still loading, or every one failed) → no toggle, no inline
// rows, no skeleton, no error string. With presence also unavailable the whole
// second row is absent and the bar is one row again. A source that is ready
// draws the feed; a source that is not is named inside the open list
// (`FeedNote`), where somebody who asked can read it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY ROW IS BUILT FROM PARTS, NEVER FROM A SENTENCE THE DATA MODULE MADE
// ═══════════════════════════════════════════════════════════════════════════
//
// A name reaches this page through `Person`, which is the one join site, and a
// track through `TrackTitle`, which owns what to draw when the store has no
// name for one. A data module that returned `"Cal put Roygbiv in the canon"`
// would have had to do both jobs itself, in a file that cannot render a
// `(left the server)` suffix or a `data-resolved` attribute — so the two would
// have drifted immediately, which is P6-177's shape.
//
// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ THE ROW ABOUT YOU IS BRIGHTER, AND IT IS STILL A 3a FACT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design renders the member's own events in `--d2d-text` and everything
// else muted. `mine` is computed by comparing the SESSION'S OWN id against
// `picker_id` / `actor_ids`, both of which are published in `3a/` and readable
// by any signed-in member. Nothing from `3b/` reaches this module and nothing
// here asks for another member's object — ADR-0003 D3. Highlighting a public
// row because it happens to be about the reader is a render decision, not a
// tier crossing.

/**
 * A watch row's `subscriptionId` this page may safely link to, or `null`.
 *
 * 🔴 THE MANAGER'S ADDITION, NOT THE SPEC'S. The published row is never
 * validated against the store's own id shape before it reaches this page,
 * and `season_champion`'s `subscription_id` is `null` by design (it names no
 * title at all). Same shape as
 * `site/functions/_lib/projections.js::SUBSCRIPTION_ID` (`WatchManager`'s own
 * base36 ids, lower-case, bounded) — duplicated rather than imported, the
 * same trade `SNOWFLAKE` already makes independently in both `music.js` and
 * `watch.js`: a Function under `functions/` and a page under `src/` are two
 * different deploy targets, so there is nothing to import from here even in
 * principle.
 */
const WATCH_TITLE_ID = /^[0-9a-z]{1,32}$/

function watchTitleHref(subscriptionId) {
  return typeof subscriptionId === 'string' && WATCH_TITLE_ID.test(subscriptionId)
    ? `/watch/${subscriptionId}`
    : null
}

/** A watch feed row's title — linked to the title page when `subscriptionId`
 * is safe to build a route from, drawn as plain text otherwise (never a link
 * to a route the page cannot vouch for). Never called for `season_champion`,
 * which carries neither a title nor a subscription id at all. */
function WatchRowTitle({ row }) {
  const label = row.title ?? 'a title'
  const href = watchTitleHref(row.subscriptionId)
  return href ? <Link to={href}>{label}</Link> : <>{label}</>
}

/**
 * One row's sentence.
 *
 * 🔴 PLAIN LANGUAGE, PER THE REGISTER: this is a page about somebody's picks
 * and history, so the flavour lives in the panel's headings and never here. A
 * row says what happened, in the words the crew already uses for it — *"put in
 * the canon"* is the verb `MusicPanel`'s provenance line uses, so the feed uses
 * it too rather than inventing "inducted".
 *
 * ⛔ THE NIGHT NUMBER IS NOT DRAWN, AND THAT IS DELIBERATE. `number` is the
 * store's `roman_count`, an INTEGER, and the bot renders it through
 * `movie_night_roman()` as *"Movie Night LX"* in the embed the crew actually
 * reads. Printing `60` here would be a second spelling of that fact, and
 * building a Roman-numeral converter on the site would be a second
 * IMPLEMENTATION of it — P6-172's disease with a different subject. The row is
 * true without it.
 */
function RowText({ row, directory }) {
  const by =
    row.actorIds.length > 0 ? (
      <>
        {' '}
        <span className="d2d-portal__feedby">
          picked by <Person directory={directory} userId={row.actorIds[0]} />
        </span>
      </>
    ) : null

  switch (row.kind) {
    case 'night':
      return row.film === null ? (
        <>A movie night closed.</>
      ) : (
        <>
          Movie night closed on {row.film}
          {by === null ? null : ','}
          {by}.
        </>
      )
    case 'rating':
      // ⛔ NO AVERAGE, NO FLAIR WORD, NO RANK — the room's verdict on a FILM is
      // a tier; a number belonging to a PERSON is what P6-172 refused. `votes`
      // is a published count and is only drawn when it is one.
      return (
        <>
          The room rated {row.film ?? 'a film'}
          {row.tier === null ? null : <> — {tierText(row.tier.label)} took it</>}
          {row.votes === null ? null : <>, {row.votes} votes</>}.
        </>
      )
    case 'moty':
      return (
        <>
          Movie of the Year {row.label} settled: {row.film ?? 'a film with no title on it'}
          {by === null ? null : ','}
          {by}.
        </>
      )
    case 'induction':
      return row.actorIds.length === 0 ? (
        <>
          <TrackName row={row} /> entered the canon.
        </>
      ) : (
        <>
          <People directory={directory} ids={row.actorIds} /> put <TrackName row={row} /> in the
          canon.
        </>
      )
    case 'soty':
      return (
        <>
          Song of the Year{row.year === null ? null : ` ${row.year}`} settled:{' '}
          <TrackName row={row} />.
        </>
      )
    case 'recap':
      // `cadence` is the store's own key — "weekly" — and it is drawn as it is
      // rather than mapped through a table this file would have to keep in step
      // with a cadence somebody adds on the music side.
      return row.cadence === null ? <>A recap went out.</> : <>The {row.cadence} recap went out.</>

    // ═════════════════════════════════════════════════════════════════════
    // V2-7b: THE WATCH SOURCE. Every branch below follows the SAME rule the
    // movie/music ones above already do — a name reaches the page through
    // `Person`/`People`, a title through `WatchRowTitle` — plus one the
    // spec did not have to ask movie or music for: `member_ids` on a watch
    // row can arrive non-empty and still leave `actorIds` empty (an id that
    // failed `joinableId`), and unlike a genuinely actor-less event (music's
    // own ballot, `season_announced` below), THAT case still has something
    // true to say — see each branch's own "no name" sentence, and `FeedNote`
    // for the reason attached to it.
    case 'listed':
      // The re-list case (`member_ids: []`, spec §0) and the dropped-id case
      // (a real actor, unjoinable) render IDENTICALLY on purpose — the row
      // cannot and need not tell them apart; `FeedNote`'s "some rows cannot
      // say who" is what carries the second one's reason.
      return row.actorIds.length === 0 ? (
        <>
          <WatchRowTitle row={row} /> is back on the list.
        </>
      ) : (
        <>
          <People directory={directory} ids={row.actorIds} /> added <WatchRowTitle row={row} /> to
          the list.
        </>
      )
    case 'member_watched': {
      // 🔴 THE RATING IS A NUMBER THE MEMBER ENTERED, NOT A DERIVED AVERAGE —
      // P6-172's refusal is of a DERIVED per-member average (two disagreeing
      // derivations on one real member); this is neither derived nor an
      // average, it is the one number `/watch rate` wrote for THIS finish,
      // the same value the watch tab's own "Rated N/10" already shows. It is
      // drawn only alongside the person it belongs to — never when the actor
      // had to be dropped, because an unattributed rating is a number
      // belonging to nobody the page can name.
      // ⚠️ NOT `row.actorIds.length > 0 && …` — the no-actor case is its OWN
      // branch below (never reads `rated` at all), so by the time `rated` is
      // actually consulted, `actorIds.length > 0` already holds. A redundant
      // repeat of that guard here would be unkillable by construction — the
      // exact `ActionBar.jsx`/`isMovie` shape V2-8a's own report already
      // found (`watch.js::seasonTrackFor`'s header, verbatim).
      const rated = Number.isInteger(row.rating)
      return row.actorIds.length === 0 ? (
        <>
          <WatchRowTitle row={row} /> was finished.
        </>
      ) : (
        <>
          <People directory={directory} ids={row.actorIds} /> finished <WatchRowTitle row={row} />
          {rated ? <> and rated it {row.rating}/10</> : null}.
        </>
      )
    }
    case 'crew_finished': {
      // Always at least two by construction (spec §0); the 1-and-0 branches
      // below exist only for a partial or total id drop, which is defensive
      // rather than a shape the publisher ever sends on purpose.
      if (row.actorIds.length === 0) {
        return (
          <>
            The crew finished <WatchRowTitle row={row} />.
          </>
        )
      }
      if (row.actorIds.length === 1) {
        return (
          <>
            <Person directory={directory} userId={row.actorIds[0]} /> finished{' '}
            <WatchRowTitle row={row} />.
          </>
        )
      }
      if (row.actorIds.length === 2) {
        return (
          <>
            <Person directory={directory} userId={row.actorIds[0]} /> and{' '}
            <Person directory={directory} userId={row.actorIds[1]} /> have both finished{' '}
            <WatchRowTitle row={row} />.
          </>
        )
      }
      return (
        <>
          <People directory={directory} ids={row.actorIds} /> have all finished{' '}
          <WatchRowTitle row={row} />.
        </>
      )
    }
    case 'review':
      return row.actorIds.length === 0 ? (
        <>
          <WatchRowTitle row={row} /> got a review.
        </>
      ) : (
        <>
          <People directory={directory} ids={row.actorIds} /> reviewed <WatchRowTitle row={row} />.
        </>
      )
    case 'season_announced':
      // `member_ids` is always `[]` on this kind (spec §0) — no actor branch
      // at all, and true equally for a new season and a film's sequel, which
      // is why the sentence never says which.
      return (
        <>
          <WatchRowTitle row={row} /> has more on the way.
        </>
      )
    case 'season_champion': {
      // No `subscription_id`/`title` at all on this kind (spec §0) — never
      // `WatchRowTitle` here. The season comes from `row.season`, never
      // re-derived from `row.at` (CLAUDE.md gotcha 21: two implementations of
      // one fact disagreeing is exactly the bug that gotcha is about).
      const season = seasonLabel(row.season)
      if (row.actorIds.length === 0) {
        return <>A watch champion was named for {season}.</>
      }
      if (row.actorIds.length === 1) {
        return (
          <>
            <Person directory={directory} userId={row.actorIds[0]} /> is the watch champion for{' '}
            {season}.
          </>
        )
      }
      return (
        <>
          <People directory={directory} ids={row.actorIds} /> are the watch champions for {season}.
        </>
      )
    }
    default:
      // Unreachable: `activity.js` drops a kind it has no words for rather than
      // letting it arrive here. Kept as the loud half of that pair — a kind that
      // got past the filter should be visible, not silently blank.
      return <>Something happened in the server.</>
  }
}

/** A feed row's track, drawn through the one component that owns track names. */
function TrackName({ row }) {
  return (
    <TrackTitle
      track={{ title: row.title, resolved: row.resolved }}
      className="d2d-portal__feedtrack"
    />
  )
}

/**
 * What the feed could not read, in words.
 *
 * 🔴 AN ABSENT PRODUCER IS NAMED RATHER THAN LEFT TO LOOK LIKE A QUIET WEEK.
 * Gotcha 1's rule applied to a surface instead of a test: *"a test that cannot
 * tell 'I looked and found none' from 'I could not look' is not a test"*, and a
 * feed is exactly that instrument for a member. A movie-only feed is the thing
 * P6-182 refused in September — if music stops answering, the page must say so
 * rather than silently becoming it.
 *
 * ⚠️ P6-297: NOTHING IN HERE KNOWS ABOUT LOADING, AND THAT IS THE FIX. This
 * component still just renders whatever `missing` says. What changed is which
 * sources `missing` is ALLOWED to name — see `activityFeed`'s own note — so a
 * source still on its first fetch never reaches this component's props as
 * "did not answer" in the first place. `FeedNote` cannot un-say a claim its
 * caller already made, so the fix belongs upstream of it, not here.
 */
function FeedNote({ missing, droppedIds }) {
  const notes = []
  if (missing.includes(MOVIE_SOURCE)) notes.push('Movie night did not answer')
  if (missing.includes(MUSIC_SOURCE)) notes.push('Music did not answer')
  if (missing.includes(WATCH_SOURCE)) notes.push('Watch did not answer')
  if (droppedIds) {
    // The same sentence the music panel draws for the same cause, and the same
    // reason for drawing it: so a designed name is not mistaken for the member
    // directory being down, which is a different outage with its own note.
    notes.push('some rows cannot say who')
  }
  if (notes.length === 0) return null
  return (
    <p className="d2d-drop__note" data-testid="feed-note">
      {notes.join(' · ')}, so this list is short.
    </p>
  )
}

const SOURCE_ORDER = Object.freeze([MOVIE_SOURCE, MUSIC_SOURCE, WATCH_SOURCE])

/** "a", "a and b", "a, b and c". */
function listOf(items) {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * `freshness()`'s label as the parts the toggle draws: `As of ` · `21:04 UTC` ·
 * `3 min ago`. The words stay `freshness()`'s — this only cuts them at the
 * separator it already writes — so the label here and every other stamp on the
 * portal cannot be two spellings of one fact. A label that does not have the
 * expected shape (the no-date sentence, or a future format) degrades to the whole
 * text in one piece rather than to a wrong cut.
 */
function stampParts(iso, now) {
  const stamp = freshness(iso, now)
  if (!stamp.known) return { ...stamp, prefix: '', clock: stamp.label, ago: null }
  const [asOf, ago] = stamp.label.split(' · ')
  const match = /^(As of )(.+)$/i.exec(asOf)
  return {
    ...stamp,
    prefix: match ? match[1] : '',
    clock: match ? match[2] : asOf,
    ago: ago ?? null,
  }
}

/**
 * Everything the header's feed draws, derived once.
 *
 * `available` is whether there is a feed to show at all: at least one source is
 * READY. Loading and failed sources never make it available — and never
 * contribute a stamp, a row or a stale name (P6-297, P6-306).
 *
 * 🔴 P6-306 + THE HANDOFF'S RULE 6: THE LABEL IS THE NEWEST NON-STALE READY
 * SOURCE'S STAMP. When any ready source is inside the window that is also simply
 * the newest ready stamp, since nothing newer than a fresh one can be stale. The
 * one case the two readings split is every ready source being stale, and there
 * the label keeps reading the newest — a stamp with its real age on it ("2 d
 * ago") is honest, where a blank would hide that the feed is old. `feedAsOf`'s
 * own note has the history.
 */
export function feedView({ movie, music, watch, userId, now }) {
  const ready = {
    [MOVIE_SOURCE]: movie?.state === MOVIE_READY,
    [MUSIC_SOURCE]: music?.state === MUSIC_READY,
    [WATCH_SOURCE]: watch?.state === WATCH_READY,
  }
  // 🔴 P6-297: THE CONSTANTS, NEVER THE LITERAL — each of `movie.js`/`music.js`/
  // `watch.js` spells its own states out, and retyping `'ready'` here would be the
  // exact shape CLAUDE.md gotcha 21 warns about (a guard and a consumer as two
  // implementations of one contract).
  const loading = {
    [MOVIE_SOURCE]: movie?.state === MOVIE_LOADING,
    [MUSIC_SOURCE]: music?.state === MUSIC_LOADING,
    [WATCH_SOURCE]: watch?.state === WATCH_LOADING,
  }
  const feed = activityFeed({
    movieShared: movie?.shared ?? null,
    musicShared: music?.shared ?? null,
    watchShared: watch?.shared ?? null,
    movieReady: ready[MOVIE_SOURCE],
    musicReady: ready[MUSIC_SOURCE],
    watchReady: ready[WATCH_SOURCE],
    movieLoading: loading[MOVIE_SOURCE],
    musicLoading: loading[MUSIC_SOURCE],
    watchLoading: loading[WATCH_SOURCE],
    userId,
  })

  // ⚠️ ONLY A READY SOURCE CONTRIBUTES A STAMP — to the label and to the stale
  // guard alike. A pending source's `as_of` could win outright under "newest",
  // printing an in-flight guess as the feed's own freshness; P6-306 measured that
  // deleting each of these three guards survived the original tests, and
  // `activity-feed.test.jsx` now hands this a not-ready source that DOES carry a
  // blob so each guard is a separate kill.
  const stamps = {
    [MOVIE_SOURCE]: ready[MOVIE_SOURCE] ? movie?.shared?.as_of : null,
    [MUSIC_SOURCE]: ready[MUSIC_SOURCE] ? music?.shared?.as_of : null,
    [WATCH_SOURCE]: ready[WATCH_SOURCE] ? watch?.shared?.as_of : null,
  }
  const stale = staleSources(stamps, now).map(({ source, days }) => ({
    source,
    name: SOURCE_LABEL[source],
    days,
    iso: stamps[source],
    date: shortDate(stamps[source]),
  }))
  const staleIds = new Set(stale.map((entry) => entry.source))
  const fresh = SOURCE_ORDER.filter((source) => !staleIds.has(source)).map((source) => stamps[source])
  const asOf = feedAsOf(fresh) ?? feedAsOf(Object.values(stamps))

  const sources = SOURCE_ORDER.map((source) => {
    const state = ready[source]
      ? staleIds.has(source)
        ? 'stale'
        : 'fresh'
      : loading[source]
        ? 'loading'
        : 'missing'
    return { id: source, name: SOURCE_LABEL[source], state, iso: stamps[source] }
  })

  return {
    available: SOURCE_ORDER.some((source) => ready[source]),
    anyLoading: SOURCE_ORDER.some((source) => loading[source]),
    rows: feed.rows,
    missing: feed.missing,
    droppedIds: feed.droppedIds,
    stale,
    stamp: stampParts(asOf, now),
    sources,
  }
}

/** The one line that names what is stale, for a chip or the phone's second line. */
function staleHeadline(stale) {
  return stale.length === 1 ? `${stale[0].name} stale` : `${stale.length} sources stale`
}

/** The accessible name: the FACT first — what this is, how fresh, what is stale —
 * then what pressing it does. "Close" is in the open name because the visible
 * label then reads CLOSE (WCAG 2.5.3, label in name). */
function toggleName(view, open) {
  const label = view.stamp.label
  const asOf = label.charAt(0).toLowerCase() + label.slice(1)
  const names = view.stale.map((entry) => entry.name.toLowerCase())
  const staleClause =
    names.length === 0 ? '' : `, ${listOf(names)} ${names.length === 1 ? 'is' : 'are'} stale`
  return `Activity, ${asOf}${staleClause}. ${open ? 'Close the list.' : 'Show recent activity.'}`
}

function InlineRows({ view, directory, now }) {
  if (view.rows.length === 0) {
    // "Nothing yet" is a claim, and a source still on its first fetch has not
    // made it. Nothing is drawn until everything that is going to answer has.
    return view.anyLoading ? null : (
      <li className="d2d-strip__quiet d2d-micro" data-testid="feed-inline-empty">
        Nothing dated yet
      </li>
    )
  }
  return view.rows.slice(0, FEED_ROWS).map((row) => (
    <li
      key={row.id}
      className="d2d-strip__item"
      data-source={row.source}
      data-mine={row.mine ? 'true' : 'false'}
    >
      <span className="d2d-strip__when d2d-micro" title={row.at}>
        {sinceLabel(row.at, now)}
      </span>
      <span className="d2d-strip__text">
        <RowText row={row} directory={directory} />
      </span>
    </li>
  ))
}

function StaleBanner({ stale }) {
  const sentences = stale.map(
    (entry) => `${entry.name} last reported on ${entry.date}, ${entry.days} days ago.`,
  )
  return (
    <p className="d2d-drop__stale" data-testid="feed-stale">
      <span className="d2d-drop__staledot" aria-hidden="true" />
      <span>
        {sentences.join(' ')} {stale.length === 1 ? 'Its' : 'Their'} rows here may be out of date.
      </span>
    </p>
  )
}

function SourceFooter({ view, now }) {
  const shown = view.sources.filter((source) => source.state !== 'loading')
  if (shown.length === 0) return null
  return (
    <ul className="d2d-drop__sources" aria-label="Sources" data-testid="feed-sources">
      {shown.map((source) => {
        const parts = stampParts(source.iso, now)
        const text =
          source.state === 'missing'
            ? `${source.name} · no answer`
            : source.state === 'stale'
              ? `${source.name} · stale · last ${shortDate(source.iso)}`
              : `${source.name} · ${parts.known ? parts.clock : 'no date'}`
        return (
          <li
            key={source.id}
            className={`d2d-drop__source d2d-micro d2d-drop__source--${source.state}`}
            data-source={source.id}
            data-state={source.state}
          >
            <span className="d2d-drop__sourcedot" aria-hidden="true" />
            {text}
          </li>
        )
      })}
    </ul>
  )
}

function FeedPanel({ view, directory, now }) {
  return (
    <div className="d2d-topbar__drop" id="d2d-feed-panel" data-testid="activity-feed">
      <div className="d2d-page">
        {view.stale.length > 0 ? <StaleBanner stale={view.stale} /> : null}
        <div
          className="d2d-drop__scroll d2d-drop__scroll--feed"
          role="region"
          aria-label="Recent activity"
          tabIndex={0}
        >
          {view.rows.length === 0 ? (
            view.anyLoading ? (
              // 🔴 P6-297: A THIRD FACT, NOT A THIRD SPELLING OF "EMPTY". Neither
              // sentence below is true yet — nothing has been measured as empty,
              // and nothing has failed to answer, because at least one source has
              // not answered at all. Shown only while there are no rows: the
              // moment any ready source contributes one, this gives way to the
              // list rather than continuing to claim nothing is known.
              <p className="d2d-drop__flat" data-testid="feed-loading">
                Loading what’s been happening…
              </p>
            ) : (
              // Two different facts and the page says which: nothing has happened,
              // or nothing answered. `FeedNote` carries the second.
              <div className="d2d-drop__empty" data-testid="feed-empty">
                <span className="d2d-drop__emptytitle">Nothing dated has happened yet.</span>
                <span className="d2d-drop__emptybody">
                  Movie nights, music and watch updates land here as they happen.
                </span>
              </div>
            )
          ) : (
            <ul className="d2d-drop__list" data-testid="feed-list">
              {view.rows.map((row) => (
                <li
                  key={row.id}
                  className="d2d-drop__row"
                  data-source={row.source}
                  data-mine={row.mine ? 'true' : 'false'}
                >
                  {/* ⚠️ `title` CARRIES THE STAMP VERBATIM, which the relative label
                      destroys — `freshness()` makes the same trade for the same
                      reason, and it is what an operator actually needs. */}
                  <span className="d2d-drop__when d2d-micro" title={row.at}>
                    {sinceLabel(row.at, now)}
                  </span>
                  <span className="d2d-drop__text">
                    <RowText row={row} directory={directory} />
                  </span>
                  <span className="d2d-drop__source-name d2d-micro">{SOURCE_LABEL[row.source]}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <FeedNote missing={view.missing} droppedIds={view.droppedIds} />
        <SourceFooter view={view} now={now} />
      </div>
    </div>
  )
}

/**
 * The header's second row, and the feed list that drops in under it.
 *
 * `open` is the header's ONE open-list state — `'none' | 'feed' | 'presence'` —
 * and it lives in `Layout.jsx`, because opening one list closes the other and
 * the presence button in the top bar toggles the same value. `lead` is the
 * presence strip (button + divider) when presence is available, or `null`; it is
 * drawn first in the row, and when it is the only thing in the row the row
 * hides itself from 1120 up, where presence has moved into the bar.
 *
 * Renders NOTHING when there is neither a feed nor a `lead` — the bar is one row.
 */
export default function ActivityFeed({
  movie,
  music,
  watch,
  directory,
  userId,
  now,
  open = 'none',
  onToggle,
  lead = null,
}) {
  const view = feedView({ movie, music, watch, userId, now })
  if (!view.available && lead === null) return null

  const feedOpen = open === 'feed' && view.available
  const anyOpen = feedOpen || open === 'presence'
  const stale = view.stale

  return (
    <>
      <div
        className={[
          'd2d-topbar__strip',
          anyOpen ? 'd2d-topbar__strip--open' : '',
          view.available ? '' : 'd2d-topbar__strip--presence-only',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="header-strip"
      >
        <div className="d2d-page d2d-topbar__stripinner">
          {lead === null ? null : (
            <div className="d2d-strip__presence">
              {lead}
              <span className="d2d-strip__divider" aria-hidden="true" />
            </div>
          )}

          {view.available ? (
            <>
              {/* A scroller with nothing focusable in it is a region a keyboard cannot
                  reach (axe: scrollable-region-focusable), and most rows here are text.
                  `tabIndex` makes it a stop — arrow keys scroll it — and it exists only
                  from 768, where it is displayed; below that it is `display: none`. */}
              <ul
                className="d2d-strip__items"
                aria-label="Latest activity"
                tabIndex={0}
                data-testid="feed-inline"
              >
                <InlineRows view={view} directory={directory} now={now} />
              </ul>

              {stale.length > 0 ? (
                <span className="d2d-strip__chip d2d-micro" data-testid="feed-stale-chip">
                  <span className="d2d-strip__chipdot" aria-hidden="true" />
                  {/* Two spellings of one fact, and CSS picks: the name and the age
                      where the row has the room (1024 and up), a bare count where it
                      does not. At 768 the full chip measured 41px over the row. */}
                  <span className="d2d-strip__chipfull">
                    {stale.length === 1
                      ? `${staleHeadline(stale)} · ${stale[0].days} days`
                      : staleHeadline(stale)}
                  </span>
                  <span className="d2d-strip__chipshort">{stale.length} stale</span>
                </span>
              ) : null}

              <button
                type="button"
                className="d2d-feedtoggle"
                aria-expanded={feedOpen ? 'true' : 'false'}
                aria-controls={feedOpen ? 'd2d-feed-panel' : undefined}
                aria-label={toggleName(view, feedOpen)}
                data-stale={stale.length > 0 ? 'true' : undefined}
                data-testid="feed-toggle"
                onClick={(event) => onToggle?.('feed', event.currentTarget)}
              >
                <span className="d2d-feedtoggle__text">
                  {/* ONE ELEMENT HOLDS THE WHOLE LABEL, AND CSS CUTS IT. `As of 21:04
                      UTC · 3 min ago` is `freshness()`'s own text in three spans:
                      from 768 it is one line with the separator, below 768 it is two
                      lines and the separator and the `As of ` go. `title` is the
                      stamp verbatim — the thing a relative label destroys. */}
                  <span
                    className="d2d-feedtoggle__label d2d-micro"
                    data-testid="feed-as-of"
                    title={view.stamp.title ?? undefined}
                  >
                    <span className="d2d-feedtoggle__first">
                      <span className="d2d-feedtoggle__prefix">{view.stamp.prefix}</span>
                      {view.stamp.clock}
                    </span>
                    {view.stamp.ago === null ? null : (
                      <>
                        <span className="d2d-feedtoggle__sep"> · </span>
                        <span className="d2d-feedtoggle__ago">{view.stamp.ago}</span>
                      </>
                    )}
                  </span>
                  {stale.length > 0 ? (
                    <span className="d2d-feedtoggle__stale d2d-micro" data-testid="feed-stale-line">
                      <span className="d2d-strip__chipdot" aria-hidden="true" />
                      {stale.length} stale
                    </span>
                  ) : null}
                  <span className="d2d-feedtoggle__close d2d-micro">Close</span>
                </span>
                <span className="d2d-feedtoggle__chev" aria-hidden="true" />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {feedOpen ? <FeedPanel view={view} directory={directory} now={now} /> : null}
    </>
  )
}
